import {
  Box,
  Text,
  VStack,
  HStack,
  Checkbox,
  IconButton,
  Spinner,
  Badge,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiEye, FiEyeOff, FiSettings } from 'react-icons/fi'
import { useScoringProfiles } from '../../hooks/useAnalyticsMap'
import { HighValueSettingsModal } from './HighValueSettingsModal'
import { SCORING_PROFILE_COLORS } from '../../types/analyticsMap'
import type { ScoringProfile, BoundingBox } from '../../types/analyticsMap'
import type { FilterGroupRequest } from '../../types/contactList'
import { extractApiMessage } from '../../lib/apiError'
import { WhoFillsThisPopover } from './WhoFillsThisPopover'

interface HighValueVoterPanelProps {
  filterGroups: FilterGroupRequest[]
  bbox: BoundingBox | null
  selectedProfiles: Set<string>
  onProfilesChange: (profiles: Set<string>) => void
  profileThresholds: Record<string, number>
  onThresholdsChange: (thresholds: Record<string, number>) => void
  dimNonHighlighted: boolean
  onDimToggle: (dim: boolean) => void
  resultCount?: number
  isLoadingResults?: boolean
  resultsError?: unknown
}

export function HighValueVoterPanel({
  selectedProfiles,
  onProfilesChange,
  profileThresholds,
  onThresholdsChange,
  dimNonHighlighted,
  onDimToggle,
  resultCount,
  isLoadingResults,
  resultsError,
}: HighValueVoterPanelProps) {
  const { data: profiles = [], isLoading: profilesLoading } = useScoringProfiles()
  const settingsModal = useDisclosure()

  const handleToggle = (profileName: string) => {
    const next = new Set(selectedProfiles)
    if (next.has(profileName)) next.delete(profileName)
    else next.add(profileName)
    onProfilesChange(next)
  }

  if (profilesLoading) {
    return (
      <Box py={4} textAlign="center">
        <Spinner size="sm" color="accent" />
      </Box>
    )
  }

  const defaultProfiles = profiles.filter((p) => p.category === 'default')
  const advancedProfiles = profiles.filter((p) => p.category === 'advanced')
  const selectedIn = (list: ScoringProfile[]) =>
    list.filter((p) => selectedProfiles.has(p.name)).length

  const renderList = (list: ScoringProfile[], blurb: string) => (
    <VStack gap={1.5} align="stretch">
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.500">{blurb}</Text>
        <WhoFillsThisPopover profiles={list} />
      </HStack>
      {list.map((profile) => {
        const color = SCORING_PROFILE_COLORS[profile.name] ?? '#9CA3AF'
        const isChecked = selectedProfiles.has(profile.name)
        const effectiveThreshold = profileThresholds[profile.name] ?? profile.defaultThreshold
        return (
          <Tooltip key={profile.name} content={profile.description} showArrow openDelay={400} positioning={{
            placement: "right"
          }}>
            <HStack
              gap={2}
              px={2}
              py={1.5}
              minH="44px"
              borderRadius="md"
              cursor="pointer"
              bg={isChecked ? 'gray.50' : 'transparent'}
              _hover={{ bg: 'gray.50' }}
              onClick={() => handleToggle(profile.name)}
            >
              <Box w={3} h={3} borderRadius="full" bg={color} flexShrink={0} opacity={isChecked ? 1 : 0.4} />
              <Checkbox.Root onCheckedChange={() => handleToggle(profile.name)} size="sm" pointerEvents="none" flex="1" checked={isChecked}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                <Text fontSize="sm">{profile.label}</Text>
              </Checkbox.Label></Checkbox.Root>
              {isChecked && (
                <Badge fontSize="0.65rem" colorPalette="gray" variant="subtle">
                  ≥{effectiveThreshold}
                </Badge>
              )}
            </HStack>
          </Tooltip>
        );
      })}
    </VStack>
  )

  return (
    <Box>
      <HStack justify="flex-end" mb={2} gap={1}>
        <Tooltip content={dimNonHighlighted ? 'Show all contacts' : 'Dim non-highlighted contacts'}>
          <IconButton
            aria-label="Toggle dim non-highlighted"
            size="xs"
            minW="44px"
            minH="44px"
            variant={dimNonHighlighted ? 'solid' : 'ghost'}
            colorPalette={dimNonHighlighted ? 'blue' : 'gray'}
            onClick={() => onDimToggle(!dimNonHighlighted)}
            disabled={selectedProfiles.size === 0}>{dimNonHighlighted ? <FiEyeOff /> : <FiEye />}</IconButton>
        </Tooltip>
        <Tooltip content="Score thresholds">
          <IconButton
            aria-label="Score thresholds"
            size="xs"
            minW="44px"
            minH="44px"
            variant="ghost"
            onClick={settingsModal.onOpen}><FiSettings /></IconButton>
        </Tooltip>
      </HStack>

      <Tabs.Root size="sm" variant='subtle' colorPalette="brand" lazyMount unmountOnExit defaultValue="default">
        <Tabs.List mb={2}>
          <Tabs.Trigger value="default">
            Default
            {selectedIn(defaultProfiles) > 0 && (
              <Badge ml={1.5} bg="info.subtle" color="info.fg" borderRadius="full">{selectedIn(defaultProfiles)}</Badge>
            )}
          </Tabs.Trigger>
          <Tabs.Trigger value="advanced">
            Advanced
            {selectedIn(advancedProfiles) > 0 && (
              <Badge ml={1.5} bg="info.subtle" color="info.fg" borderRadius="full">{selectedIn(advancedProfiles)}</Badge>
            )}
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.ContentGroup>
          <Tabs.Content value="default" px={0} py={0}>{renderList(defaultProfiles, 'Simple, self-explaining target types.')}</Tabs.Content>
          <Tabs.Content value="advanced" px={0} py={0}>{renderList(advancedProfiles, 'Same people, ranked by strategic impact.')}</Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>

      {selectedProfiles.size > 0 && (
        <Text fontSize="xs" color="gray.400" mt={2}>
          {selectedProfiles.size} profile{selectedProfiles.size > 1 ? 's' : ''} selected
        </Text>
      )}

      {selectedProfiles.size > 0 && !!resultsError && !isLoadingResults && (
        <Box mt={2} role="alert">
          <Text fontSize="xs" color="danger.fg">
            {extractApiMessage(resultsError, 'Could not score voters for this view.')}
          </Text>
        </Box>
      )}

      {selectedProfiles.size > 0 && resultCount === 0 && !isLoadingResults && !resultsError && (
        <Box mt={2} p={2} bg="orange.50" borderRadius="md">
          <Text fontSize="xs" color="orange.700">
            No high-value voters at the current threshold. Lower a threshold with the gear icon above, or widen your filters.
          </Text>
        </Box>
      )}

      <HighValueSettingsModal
        isOpen={settingsModal.open}
        onClose={settingsModal.onClose}
        profiles={profiles}
        thresholds={profileThresholds}
        onApply={onThresholdsChange}
      />
    </Box>
  );
}
